import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { CHART_INK } from "@/lib/tool-kit/palette";
import {
  ROB_SIZES,
  type SummaryLayout,
  type TrafficLayout,
} from "@/lib/tool-kit/research-discovery/rob-layout";
import {
  inkOn,
  ROB_PALETTES,
  ROB_SYMBOLS,
  type RobLevel,
  type RobPaletteId,
} from "@/lib/tool-kit/research-discovery/rob-plot";

/*
 * The two figures, drawn from layouts. Everything is set as an attribute (fills, fonts,
 * strokes) so the exported SVG and PNG look the same outside the page.
 */

const OUTLINE = "#3a3a3a";
const { font, small, titleFont, legendDot } = ROB_SIZES;

interface Frame {
  id: string;
  label: string;
  description: string;
  palette: RobPaletteId;
}

function Dot({
  level,
  palette,
  cx,
  cy,
  radius,
}: {
  level: RobLevel;
  palette: RobPaletteId;
  cx: number;
  cy: number;
  radius: number;
}) {
  const fill = ROB_PALETTES[palette][level];
  return (
    <g>
      <circle
        cx={px(cx)}
        cy={px(cy)}
        fill={fill}
        r={radius}
        stroke={OUTLINE}
        strokeWidth={0.75}
      />
      <text
        fill={inkOn(fill)}
        fontSize={radius * 1.6}
        fontWeight="bold"
        textAnchor="middle"
        x={px(cx)}
        y={px(cy + radius * 0.56)}
      >
        {ROB_SYMBOLS[level]}
      </text>
    </g>
  );
}

function Title({
  title,
}: {
  title: { x: number; y: number; text: string } | null;
}) {
  if (!title) {
    return null;
  }
  return (
    <text
      fill={CHART_INK.text}
      fontSize={titleFont}
      fontWeight="bold"
      textAnchor="middle"
      x={px(title.x)}
      y={px(title.y)}
    >
      {title.text}
    </text>
  );
}

export function TrafficChart({
  layout,
  frame,
}: {
  layout: TrafficLayout;
  frame: Frame;
}) {
  const { legend } = layout;
  return (
    <ChartSvg
      description={frame.description}
      height={layout.height}
      id={frame.id}
      label={frame.label}
      width={layout.width}
    >
      <Title title={layout.title} />
      <text
        fill={CHART_INK.text}
        fontSize={font}
        fontWeight="bold"
        x={px(layout.studyHeader.x)}
        y={px(layout.studyHeader.y)}
      >
        {layout.studyHeader.text}
      </text>
      {layout.headers.map((header) => (
        <text
          fill={CHART_INK.text}
          fontSize={font}
          fontWeight="bold"
          key={header.text}
          textAnchor="middle"
          x={px(header.x)}
          y={px(header.y)}
        >
          {header.text}
        </text>
      ))}
      {layout.separatorX === null ? null : (
        <line
          stroke={CHART_INK.grid}
          x1={px(layout.separatorX)}
          x2={px(layout.separatorX)}
          y1={px(layout.gridTop)}
          y2={px(layout.gridBottom)}
        />
      )}
      {layout.rows.map((row) => (
        <g key={`${row.name.text}-${row.name.y}`}>
          <text
            fill={CHART_INK.text}
            fontSize={font}
            x={px(row.name.x)}
            y={px(row.name.y)}
          >
            {row.name.text}
          </text>
          {row.cells.map((cell) => (
            <Dot
              cx={cell.cx}
              cy={cell.cy}
              key={cell.cx}
              level={cell.level}
              palette={frame.palette}
              radius={layout.radius}
            />
          ))}
        </g>
      ))}
      <text
        fill={CHART_INK.text}
        fontSize={small}
        fontWeight="bold"
        x={px(layout.keyHeading.x)}
        y={px(layout.keyHeading.y)}
      >
        {layout.keyHeading.text}
      </text>
      {layout.keys.map((line) => (
        <text
          fill={CHART_INK.muted}
          fontSize={small}
          key={line.text}
          x={px(line.x)}
          y={px(line.y)}
        >
          {line.text}
        </text>
      ))}
      <text
        fill={CHART_INK.text}
        fontSize={small}
        fontWeight="bold"
        x={px(legend.heading.x)}
        y={px(legend.heading.y)}
      >
        {legend.heading.text}
      </text>
      {legend.entries.map((entry) => (
        <g key={entry.level}>
          <Dot
            cx={entry.cx}
            cy={entry.cy}
            level={entry.level}
            palette={frame.palette}
            radius={legendDot}
          />
          <text
            fill={CHART_INK.text}
            fontSize={small}
            x={px(entry.labelX)}
            y={px(entry.labelY)}
          >
            {entry.label}
          </text>
        </g>
      ))}
    </ChartSvg>
  );
}

export function SummaryChart({
  layout,
  frame,
  percent,
}: {
  layout: SummaryLayout;
  frame: Frame;
  /** A share (0–1) as a whole-number percentage in the page's locale. */
  percent: (share: number) => string;
}) {
  const { legend } = layout;
  const colours = ROB_PALETTES[frame.palette];
  return (
    <ChartSvg
      description={frame.description}
      height={layout.height}
      id={frame.id}
      label={frame.label}
      width={layout.width}
    >
      <Title title={layout.title} />
      {layout.ticks.map((tick) => (
        <g key={tick.label}>
          <line
            stroke={CHART_INK.grid}
            x1={px(tick.x)}
            x2={px(tick.x)}
            y1={px(layout.gridTop)}
            y2={px(layout.gridBottom)}
          />
          <text
            fill={CHART_INK.muted}
            fontSize={small}
            textAnchor="middle"
            x={px(tick.x)}
            y={px(layout.gridBottom + small + 4)}
          >
            {tick.label}
          </text>
        </g>
      ))}
      {layout.rows.map((row) => (
        <g key={row.y}>
          <text
            fill={CHART_INK.text}
            fontSize={font}
            textAnchor="end"
            x={px(row.name.x)}
            y={px(row.name.y)}
          >
            {row.name.text}
          </text>
          {row.segments.map((segment) => (
            <g key={segment.level}>
              <rect
                fill={colours[segment.level]}
                height={layout.barHeight}
                stroke={OUTLINE}
                strokeWidth={0.5}
                width={px(segment.width)}
                x={px(segment.x)}
                y={px(row.y)}
              />
              {segment.width >= 30 ? (
                <text
                  fill={inkOn(colours[segment.level])}
                  fontSize={small}
                  fontWeight="bold"
                  textAnchor="middle"
                  x={px(segment.x + segment.width / 2)}
                  y={px(row.y + layout.barHeight / 2 + small * 0.35)}
                >
                  {percent(segment.share)}
                </text>
              ) : null}
            </g>
          ))}
        </g>
      ))}
      <text
        fill={CHART_INK.text}
        fontSize={font}
        textAnchor="middle"
        x={px(layout.axisTitle.x)}
        y={px(layout.axisTitle.y)}
      >
        {layout.axisTitle.text}
      </text>
      <text
        fill={CHART_INK.text}
        fontSize={small}
        fontWeight="bold"
        x={px(legend.heading.x)}
        y={px(legend.heading.y)}
      >
        {legend.heading.text}
      </text>
      {legend.entries.map((entry) => (
        <g key={entry.level}>
          <rect
            fill={colours[entry.level]}
            height={legendDot * 2}
            stroke={OUTLINE}
            strokeWidth={0.75}
            width={legendDot * 2}
            x={px(entry.cx - legendDot)}
            y={px(entry.cy - legendDot)}
          />
          <text
            fill={CHART_INK.text}
            fontSize={small}
            x={px(entry.labelX)}
            y={px(entry.labelY)}
          >
            {entry.label}
          </text>
        </g>
      ))}
    </ChartSvg>
  );
}
